import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FileCode2, Save, Trash2, Play, Plus, Eye, AlertTriangle, SendHorizonal, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = `${API}/v1/templates`;

const STARTER = `<mjml>
  <mj-body background-color="#f2f4f6">
    <mj-section background-color="#ffffff" padding="28px" border-radius="10px">
      <mj-column>
        <mj-text font-size="22px" color="#111827" font-family="Helvetica" font-weight="700">
          Hello {{firstName}} 👋
        </mj-text>
        <mj-text color="#374151" font-size="15px" line-height="1.6">
          Welcome to {{company}}. This email was designed in MJML and
          rendered responsively by the Delivery Engine.
        </mj-text>
        <mj-button background-color="#2563eb" color="#ffffff" href="https://example.com">
          Get started
        </mj-button>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

const STARTER_HTML = `<div style="font-family:Helvetica,Arial,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;padding:28px;color:#111827">
  <h1 style="font-size:22px;margin:0 0 12px">Hello {{firstName}} 👋</h1>
  <p style="font-size:15px;line-height:1.6;color:#374151">
    Welcome to {{company}}. Paste your own HTML email here — it will be sent as-is.
  </p>
  <a href="https://example.com" style="display:inline-block;margin-top:16px;background:#2563eb;color:#fff;padding:12px 22px;border-radius:6px;text-decoration:none">Get started</a>
</div>`;

function detectTags(mjml) {
  const set = new Set();
  const re = /\{\{\s*([\w.-]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(mjml))) set.add(m[1]);
  return [...set];
}

export default function Templates() {
  const [list, setList] = useState([]);
  const [id, setId] = useState(null);
  const [name, setName] = useState("");
  const [mjml, setMjml] = useState(STARTER);
  const [type, setType] = useState("mjml");
  const [vars, setVars] = useState({ firstName: "Ada", company: "Acme Inc" });
  const [html, setHtml] = useState("");
  const [errors, setErrors] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [banner, setBanner] = useState(null);
  const [sendFrom, setSendFrom] = useState("noreply@camails-docusigns.net");
  const [sendTo, setSendTo] = useState("");
  const [subject, setSubject] = useState("Hello {{firstName}}");
  const [sending, setSending] = useState(false);
  const debounce = useRef();

  const tags = useMemo(() => detectTags(mjml + " " + subject), [mjml, subject]);
  const notify = (kind, text) => { setBanner({ kind, text }); setTimeout(() => setBanner(null), 6000); };

  const loadList = useCallback(async () => setList((await axios.get(T)).data), []);
  useEffect(() => { loadList(); }, [loadList]);

  // Live, debounced render.
  useEffect(() => {
    setRendering(true);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${T}/render`, { mjml, variables: vars, type });
        setHtml(data.html || "");
        setErrors(data.errors || []);
      } catch (e) {
        setErrors([{ line: 0, message: e?.response?.data?.message || e.message }]);
      } finally { setRendering(false); }
    }, 450);
    return () => clearTimeout(debounce.current);
  }, [mjml, vars, type]);

  const newTemplate = () => { setId(null); setName(""); setMjml(type === "html" ? STARTER_HTML : STARTER); notify("success", "New blank template — edit and save."); };

  const load = async (tid) => {
    const { data } = await axios.get(`${T}/${tid}`);
    setId(data.id); setName(data.name); setMjml(data.mjml); setType(data.contentType || "mjml");
    const seed = {};
    detectTags(data.mjml).forEach((k) => (seed[k] = vars[k] || ""));
    setVars(seed);
    notify("success", `Loaded "${data.name}".`);
  };

  const save = async () => {
    if (!name.trim()) return notify("error", "Give the template a name first.");
    try {
      const payload = { name: name.trim(), mjml, variables: tags, contentType: type };
      if (id) await axios.put(`${T}/${id}`, payload);
      else { const { data } = await axios.post(T, payload); setId(data.id); }
      notify("success", `Saved "${name}".`);
      loadList();
    } catch (e) { notify("error", e?.response?.data?.message || e.message); }
  };

  const del = async (tid) => { await axios.delete(`${T}/${tid}`); if (tid === id) newTemplate(); loadList(); };

  // Switch MJML<->HTML. If the editor still holds the other mode's starter
  // (untouched), swap to the matching starter so a user never sends MJML as raw
  // HTML by accident.
  const switchType = (next) => {
    if (next === type) return;
    if (mjml.trim() === STARTER.trim() || mjml.trim() === STARTER_HTML.trim() || !mjml.trim()) {
      setMjml(next === "html" ? STARTER_HTML : STARTER);
    }
    setType(next);
  };

  const sendTest = async () => {
    if (!sendTo.trim()) return notify("error", "Enter a recipient for the test.");
    setSending(true);
    try {
      const { data: key } = await axios.post(`${API}/dev/api-keys`, { name: "template-test" });
      const { data } = await axios.post(`${API}/v1/send`, {
        from: { email: sendFrom }, to: [{ email: sendTo }], subject, mjml, variables: vars, contentType: type,
      }, { headers: { Authorization: `Bearer ${key.apiKey}` } });
      notify("success", `Test queued (${data.status}) → ${sendTo}. id ${data.id.slice(0, 8)}`);
    } catch (e) {
      const d = e?.response?.data;
      notify("error", d?.details ? `MJML invalid: line ${d.details[0].line} — ${d.details[0].message}` : (d?.message || e.message));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="templates-view">
      {banner && <div className={`set-banner ${banner.kind}`} data-testid="template-banner">{banner.text}</div>}

      <div className="tpl-bar">
        <div className="field inline"><input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name…" /></div>
        <div className="type-toggle" data-testid="content-type-toggle">
          <button className={`tt-btn ${type === "mjml" ? "active" : ""}`} data-testid="type-mjml" onClick={() => switchType("mjml")}>MJML</button>
          <button className={`tt-btn ${type === "html" ? "active" : ""}`} data-testid="type-html" onClick={() => switchType("html")}>HTML</button>
        </div>
        <button className="btn" data-testid="save-template-btn" onClick={save}><Save size={15} /> {id ? "Update" : "Save"}</button>
        <button className="btn ghost" data-testid="new-template-btn" onClick={newTemplate}><Plus size={15} /> New</button>
        <div className="tpl-picker">
          <select data-testid="template-picker" value={id || ""} onChange={(e) => e.target.value && load(e.target.value)}>
            <option value="">Load saved…</option>
            {list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <span className="hint">{rendering ? <><Loader2 size={12} className="spin" /> rendering…</> : `${list.length} saved`}</span>
      </div>

      <div className="tpl-split" data-testid="template-editor">
        <div className="panel tpl-editor-panel">
          <div className="p-head"><FileCode2 size={15} /><h3>{type === "html" ? "HTML source" : "MJML source"}</h3><span className="hint">{type === "html" ? "raw html, sent as-is" : "responsive markup"}</span></div>
          <textarea data-testid="mjml-input" className="mjml-code" spellCheck={false} value={mjml} onChange={(e) => setMjml(e.target.value)} />
          {errors.length > 0 && (
            <div className="tpl-errors" data-testid="mjml-errors">
              {errors.map((e, i) => (
                <div key={i} className="tpl-err"><AlertTriangle size={12} /> <b>Line {e.line}</b> — {e.message}</div>
              ))}
            </div>
          )}
        </div>

        <div className="panel tpl-preview-panel">
          <div className="p-head"><Eye size={15} /><h3>Live preview</h3><span className="hint">merge tags applied</span></div>
          <iframe data-testid="mjml-preview" title="preview" className="mjml-frame" srcDoc={html} sandbox="" />
        </div>
      </div>

      <div className="cols tpl-cols">
        <div className="panel" data-testid="merge-tags-panel">
          <div className="p-head"><Play size={15} /><h3>Merge tags</h3><span className="hint">{`{{ ... }}`}</span></div>
          <div className="p-body">
            {tags.length === 0 && <p className="note">No merge tags detected. Add e.g. <code className="ic">{`{{firstName}}`}</code> to your MJML.</p>}
            {tags.map((k) => (
              <div className="field" key={k}>
                <label>{`{{${k}}}`}</label>
                <input data-testid={`var-${k}`} value={vars[k] || ""} onChange={(e) => setVars({ ...vars, [k]: e.target.value })} placeholder={`value for ${k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="panel" data-testid="send-test-panel">
          <div className="p-head"><SendHorizonal size={15} /><h3>Send a test</h3><span className="hint">real delivery</span></div>
          <div className="p-body">
            <div className="field"><label>From (licensed mailbox)</label><input data-testid="test-from-input" value={sendFrom} onChange={(e) => setSendFrom(e.target.value)} /></div>
            <div className="field"><label>Subject</label><input data-testid="test-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
            <div className="field"><label>Recipient</label><input data-testid="test-to-input" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="you@domain.com" /></div>
            <button className="btn" data-testid="send-test-btn" disabled={sending} onClick={sendTest}>
              {sending ? <><Loader2 size={15} className="spin" /> Sending…</> : <><SendHorizonal size={15} /> Send test</>}
            </button>
            <p className="note">Compiles this MJML → HTML, applies merge tags, then sends through the live delivery pipeline.</p>
          </div>
        </div>
      </div>

      {list.length > 0 && (
        <div className="panel" data-testid="templates-list-panel" style={{ marginTop: 20 }}>
          <div className="p-head"><FileCode2 size={15} /><h3>Saved templates</h3></div>
          <div className="scroll">
            <table className="tbl">
              <thead><tr><th>Name</th><th>Merge tags</th><th>Updated</th><th></th></tr></thead>
              <tbody data-testid="templates-tbody">
                {list.map((t) => (
                  <tr key={t.id} data-testid={`template-row-${t.name}`}>
                    <td className="mono-strong">{t.name}</td>
                    <td>{(t.variables || []).map((v) => <span key={v} className="chip-tag">{v}</span>)}</td>
                    <td>{new Date(t.updatedAt).toLocaleString()}</td>
                    <td className="actions">
                      <button className="icon-btn" data-testid={`load-template-${t.name}`} onClick={() => load(t.id)}><Eye size={14} /></button>
                      <button className="icon-btn" data-testid={`delete-template-${t.name}`} onClick={() => del(t.id)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
